'use strict';

/** Campaign tutorial missions (Battle Overview anthology tab, missions 1–4). */
export const TUTORIAL_CAMPAIGN_MISSION_IDS = [
  'chapter_1_mission_1',
  'chapter_1_mission_2',
  'chapter_1_mission_3',
  'chapter_1_mission_4',
];

/**
 * @param {string} missionId
 * @returns {number}
 */
export function parseCampaignMissionNumber(missionId) {
  if (typeof missionId !== 'string' || missionId.length === 0) {
    throw new Error('parseCampaignMissionNumber: missionId must be a non-empty string');
  }
  const match = missionId.match(/chapter_\d+_mission_(\d+)/);
  if (!match) {
    throw new Error(`parseCampaignMissionNumber: unrecognised missionId "${missionId}"`);
  }
  const missionNumber = parseInt(match[1], 10);
  if (Number.isNaN(missionNumber)) {
    throw new Error(`parseCampaignMissionNumber: invalid mission number in "${missionId}"`);
  }
  return missionNumber;
}

/**
 * @param {string} missionId
 * @returns {boolean}
 */
export function isTutorialCampaignMissionId(missionId) {
  return TUTORIAL_CAMPAIGN_MISSION_IDS.includes(missionId);
}

/**
 * @typedef {{
 *   isBotGame: boolean,
 *   opponentName: string | null,
 *   reconnectGameKind: string,
 *   exploreUnknownMissionIndex?: number | null,
 *   campaignMissionId?: string | null,
 * }} ActiveGameInfo
 */

/**
 * @param {{ hasActiveGame: boolean, activeGameInfo: ActiveGameInfo | null, t: (key: string, opts?: object) => string }} params
 * @returns {string}
 */
export function getRejoinBattleLabel({ hasActiveGame, activeGameInfo, t }) {
  if (!hasActiveGame) {
    return t('battleOverview.nothingToRejoin');
  }
  if (!activeGameInfo) {
    throw new Error('getRejoinBattleLabel: activeGameInfo is required when hasActiveGame is true');
  }

  const { isBotGame, opponentName, reconnectGameKind, exploreUnknownMissionIndex, campaignMissionId } =
    activeGameInfo;

  if (
    exploreUnknownMissionIndex != null &&
    exploreUnknownMissionIndex !== '' &&
    (reconnectGameKind === 'explore_unknown' || reconnectGameKind === 'training' || reconnectGameKind === 'unknown')
  ) {
    return t('battleOverview.rejoinExploreUnknownMission', {
      missionIndex: exploreUnknownMissionIndex,
    });
  }

  if (!isBotGame) {
    if (typeof opponentName !== 'string' || opponentName.length === 0) {
      throw new Error('getRejoinBattleLabel: opponentName is required for PvP rejoin label');
    }
    return t('battleOverview.rejoinBattleAgainstOpponent', { opponentName });
  }

  if (reconnectGameKind === 'training') {
    return t('battleOverview.rejoinTrainingAgainstBot');
  }

  if (reconnectGameKind === 'explore_unknown') {
    if (exploreUnknownMissionIndex == null || exploreUnknownMissionIndex === '') {
      throw new Error(
        'getRejoinBattleLabel: exploreUnknownMissionIndex is required for Explore Unknown rejoin label'
      );
    }
    return t('battleOverview.rejoinExploreUnknownMission', {
      missionIndex: exploreUnknownMissionIndex,
    });
  }

  if (reconnectGameKind === 'campaign') {
    if (typeof campaignMissionId !== 'string' || campaignMissionId.length === 0) {
      throw new Error('getRejoinBattleLabel: campaignMissionId is required for campaign rejoin label');
    }
    const missionNumber = parseCampaignMissionNumber(campaignMissionId);
    if (isTutorialCampaignMissionId(campaignMissionId)) {
      return t('battleOverview.rejoinTutorialMission', { missionNumber });
    }
    return t('battleOverview.rejoinAnthologyMission', { missionNumber });
  }

  if (reconnectGameKind === 'war_effort') {
    return t('battleOverview.rejoinWarEffort');
  }

  // Legacy / defensive: unclassified bot PvE without mode flags is train mode (userId_bot_timestamp).
  if (reconnectGameKind === 'unknown') {
    if (!isBotGame) {
      throw new Error(
        'getRejoinBattleLabel: reconnectGameKind "unknown" is only valid for bot/PvE sessions'
      );
    }
    return t('battleOverview.rejoinTrainingAgainstBot');
  }

  throw new Error(`getRejoinBattleLabel: unsupported reconnectGameKind "${reconnectGameKind}"`);
}
